"use client";

import { useEffect } from "react";
import { reportError, isNextInternalThrow } from "@/lib/report-error";

// Mount once at the root layout. Captures errors that escape every other
// hook: top-level synchronous throws caught by the browser and unhandled
// promise rejections.
export function RootErrorHandlers(): null {
  useEffect(() => {
    const onError = (e: ErrorEvent) => {
      if (isNextInternalThrow(e.error)) return;
      // No capture node — reportError defaults to document.body, which
      // gives a viewport screenshot of the broken page state.
      reportError({
        kind: "js_exception",
        message: e.message || "Unknown error",
        stack: e.error instanceof Error ? e.error.stack : undefined,
        context: { filename: e.filename, lineno: e.lineno, colno: e.colno },
      });
    };

    const onRejection = (e: PromiseRejectionEvent) => {
      const reason = e.reason as unknown;
      if (isNextInternalThrow(reason)) return;
      const isError = reason instanceof Error;
      reportError({
        kind: "js_exception",
        message: isError ? reason.message : String(reason ?? "Unhandled promise rejection"),
        stack: isError ? reason.stack : undefined,
        context: { unhandled_promise: true },
      });
    };

    window.addEventListener("error", onError);
    window.addEventListener("unhandledrejection", onRejection);
    return () => {
      window.removeEventListener("error", onError);
      window.removeEventListener("unhandledrejection", onRejection);
    };
  }, []);

  return null;
}
